/**
 * screens/admin/adminCatalog.tsx — Plan catalog (CC-01 §7), Softknack staff only.
 * Sellable products, plans (features + entitlement grants), bundles and embed
 * registrations. No prices here: a plan points at a Billing price ref, and the
 * API refuses activation unless Billing has that price in every market. Status
 * moves draft → active → retired; nothing is deleted.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { View, Text, ScrollView, ActivityIndicator } from 'react-native';

import { useTheme } from '../../context/themeContext';
import { usePlatform } from '../../context/platformContext';
import { useToast } from '../../components/toast';
import { SettingsHeader, Panel, DataTable, StatusBadge, SideDrawer, Tag } from '../../components/platform/primitives';
import { PButton, PTextInput, PSelect, PSwitch } from '../../components/platform/controls';
import Tabs from '../../components/tabs';
import Alert from '../../components/alert';
import { PermissionDenied } from '../shell/chrome';
import { FS, FW } from '../../utils/platformTokens';
import { getApiErrorMessage } from '../../utils/errorHandler';
import {
  catalogProducts, saveCatalogProduct, catalogPlans, saveCatalogPlan, catalogBundles, saveCatalogBundle,
  embedRegistrations, saveEmbedRegistration,
} from '../../services/platform/platformApi';
import type {
  CatalogProduct, CatalogPlan, CatalogBundle, CatalogGrant, CatalogStatus, EmbedRegistration,
} from '../../schema/platform/types';

const STATUS_OPTS = [
  { value: 'draft', label: 'Draft' },
  { value: 'active', label: 'Active' },
  { value: 'retired', label: 'Retired' },
];

const BLANK_GRANT: CatalogGrant = { item: '', kind: 'consumable', quantity: 0, period: 'month', scope: 'org' };

/** Meter items + quantities a plan or bundle grants (BIL-001 §1). A new item is a row, never code. */
const GrantsEditor: React.FC<{ grants: CatalogGrant[]; onChange: (g: CatalogGrant[]) => void }> = ({ grants, onChange }) => {
  const { theme } = useTheme();
  const set = (i: number, patch: Partial<CatalogGrant>) => onChange(grants.map((g, j) => (j === i ? { ...g, ...patch } : g)));
  return (
    <View style={{ gap: 10 }}>
      <Text style={{ fontSize: FS.sm, fontWeight: FW.medium, color: theme.colors.ink2 }}>Entitlement grants</Text>
      {grants.map((g, i) => (
        <View key={i} style={{ gap: 6, borderWidth: 1, borderColor: theme.colors.line, borderRadius: 8, padding: 8 }}>
          <PTextInput mono placeholder="whatsapp.credit" value={g.item} onChangeText={(v) => set(i, { item: v })} />
          <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6 }}>
            <PSelect size="sm" width={130} value={g.kind} onChange={(v) => set(i, { kind: v as CatalogGrant['kind'] })}
              options={[{ value: 'consumable', label: 'Consumable' }, { value: 'capacity', label: 'Capacity' }]} />
            <PTextInput mono keyboardType="numeric" value={String(g.quantity)} style={{ width: 100 }}
              onChangeText={(v) => set(i, { quantity: Number(v.replace(/[^0-9]/g, '')) || 0 })} />
            <PSelect size="sm" width={100} value={g.period} onChange={(v) => set(i, { period: v as CatalogGrant['period'] })}
              options={[{ value: 'month', label: 'Month' }, { value: 'year', label: 'Year' }, { value: 'trial', label: 'Trial' }]} />
            <PSelect size="sm" width={110} value={g.scope} onChange={(v) => set(i, { scope: v as CatalogGrant['scope'] })}
              options={[{ value: 'org', label: 'Org-wide' }, { value: 'product', label: 'Product' }]} />
            <PButton size="sm" kind="ghost" icon="x" onPress={() => onChange(grants.filter((_, j) => j !== i))}>Remove</PButton>
          </View>
        </View>
      ))}
      <PButton size="sm" icon="plus" onPress={() => onChange([...grants, { ...BLANK_GRANT }])}>Add grant</PButton>
    </View>
  );
};

/** A generic editor drawer: the form body is passed in, saving goes through `save`. */
function useEditor<T>(save: (v: T) => Promise<void>, reload: () => void) {
  const toast = useToast();
  const [form, setForm] = useState<T | null>(null);
  const [isNew, setIsNew] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const open = (v: T, fresh: boolean) => { setForm(JSON.parse(JSON.stringify(v))); setIsNew(fresh); setErr(null); };
  const commit = async () => {
    if (!form) return;
    setSaving(true);
    try {
      await save(form);
      toast.success('Saved.');
      setForm(null);
      reload();
    } catch (e) {
      setErr(getApiErrorMessage(e, "Couldn't save."));
    } finally {
      setSaving(false);
    }
  };
  return { form, setForm, isNew, err, saving, open, close: () => setForm(null), commit };
}

const EditorFooter: React.FC<{ onClose: () => void; onSave: () => void; saving: boolean }> = ({ onClose, onSave, saving }) => (
  <>
    <PButton kind="ghost" onPress={onClose}>Cancel</PButton>
    <PButton kind="accent" loading={saving} onPress={onSave}>Save</PButton>
  </>
);

/* ── Products ── */
const ProductsTab: React.FC = () => {
  const [rows, setRows] = useState<CatalogProduct[] | null>(null);
  const load = useCallback(() => { catalogProducts().then(setRows, () => setRows([])); }, []);
  useEffect(load, [load]);
  const ed = useEditor<CatalogProduct>((p) => saveCatalogProduct(p, !(rows ?? []).some((r) => r.code === p.code)), load);
  const f = ed.form;
  return (
    <Panel title="Sellable products" actions={<PButton size="sm" kind="accent" icon="plus" onPress={() => ed.open({ code: '', name: '', status: 'draft' }, true)}>New product</PButton>} bodyStyle={{ padding: 0 }}>
      {rows === null ? <ActivityIndicator /> : (
        <DataTable<CatalogProduct> rows={rows} keyExtractor={(r) => r.code} minWidth={480}
          columns={[
            { key: 'code', header: 'Code', width: 140, render: (r) => r.code },
            { key: 'name', header: 'Name', render: (r) => r.name },
            { key: 'status', header: 'Status', width: 110, render: (r) => <StatusBadge status={r.status} /> },
            { key: 'e', header: '', width: 80, align: 'right', render: (r) => <PButton size="sm" kind="ghost" onPress={() => ed.open(r, false)}>Edit</PButton> },
          ]} />
      )}
      <SideDrawer open={!!f} onClose={ed.close} title={ed.isNew ? 'New product' : `Edit ${f?.name}`} footer={<EditorFooter onClose={ed.close} onSave={ed.commit} saving={ed.saving} />}>
        {f && (
          <View style={{ gap: 14 }}>
            {ed.err && <Alert tone="danger">{ed.err}</Alert>}
            <PTextInput label="Code" mono editable={ed.isNew} value={f.code} onChangeText={(v) => ed.setForm({ ...f, code: v })} />
            <PTextInput label="Name" value={f.name} onChangeText={(v) => ed.setForm({ ...f, name: v })} />
            <PSelect label="Status" value={f.status} onChange={(v) => ed.setForm({ ...f, status: v as CatalogStatus })} options={STATUS_OPTS} />
          </View>
        )}
      </SideDrawer>
    </Panel>
  );
};

/* ── Plans ── */
const BLANK_PLAN: CatalogPlan = {
  productCode: '', code: '', name: '', tierRank: 1, isTrial: false, isPublic: true, status: 'draft',
  billingPriceRef: null, features: [], grants: [],
};

const PlansTab: React.FC<{ products: CatalogProduct[] }> = ({ products }) => {
  const [rows, setRows] = useState<CatalogPlan[] | null>(null);
  const load = useCallback(() => { catalogPlans().then(setRows, () => setRows([])); }, []);
  useEffect(load, [load]);
  const ed = useEditor<CatalogPlan>(saveCatalogPlan, load);
  const f = ed.form;
  const productOpts = products.map((p) => ({ value: p.code, label: p.name }));
  return (
    <Panel title="Plans" actions={<PButton size="sm" kind="accent" icon="plus" onPress={() => ed.open({ ...BLANK_PLAN, productCode: products[0]?.code ?? '' }, true)}>New plan</PButton>} bodyStyle={{ padding: 0 }}>
      {rows === null ? <ActivityIndicator /> : (
        <DataTable<CatalogPlan> rows={rows} keyExtractor={(r) => r.id ?? r.code} minWidth={760}
          columns={[
            { key: 'p', header: 'Product', width: 110, render: (r) => r.productCode },
            { key: 'n', header: 'Plan', render: (r) => r.name },
            { key: 't', header: 'Tier', width: 60, align: 'right', render: (r) => String(r.tierRank) },
            { key: 'flags', header: '', width: 150, render: (r) => (
              <View style={{ flexDirection: 'row', gap: 4 }}>
                {r.isTrial && <Tag tone="info">Trial</Tag>}
                {!r.isPublic && <Tag tone="neutral">Private</Tag>}
              </View>
            ) },
            { key: 'price', header: 'Billing price', width: 170, render: (r) => r.billingPriceRef ?? '—' },
            { key: 's', header: 'Status', width: 100, render: (r) => <StatusBadge status={r.status} /> },
            { key: 'e', header: '', width: 80, align: 'right', render: (r) => <PButton size="sm" kind="ghost" onPress={() => ed.open(r, false)}>Edit</PButton> },
          ]} />
      )}
      <SideDrawer open={!!f} onClose={ed.close} title={ed.isNew ? 'New plan' : `Edit ${f?.name}`} footer={<EditorFooter onClose={ed.close} onSave={ed.commit} saving={ed.saving} />}>
        {f && (
          <View style={{ gap: 14 }}>
            {ed.err && <Alert tone="danger">{ed.err}</Alert>}
            <PSelect label="Product" value={f.productCode} onChange={(v) => ed.setForm({ ...f, productCode: v })} options={productOpts} disabled={!ed.isNew} />
            <PTextInput label="Code" mono value={f.code} editable={ed.isNew} onChangeText={(v) => ed.setForm({ ...f, code: v })} />
            <PTextInput label="Name" value={f.name} onChangeText={(v) => ed.setForm({ ...f, name: v })} />
            <PTextInput label="Tier rank" hint="Higher rank = higher tier; drives upgrade offers." mono keyboardType="numeric" value={String(f.tierRank)}
              onChangeText={(v) => ed.setForm({ ...f, tierRank: Number(v.replace(/[^0-9]/g, '')) || 0 })} />
            <PTextInput label="Billing price ref" hint="Must exist in Billing for every market before activation." mono value={f.billingPriceRef ?? ''}
              onChangeText={(v) => ed.setForm({ ...f, billingPriceRef: v || null })} />
            <View style={{ flexDirection: 'row', gap: 20 }}>
              <PSwitch label="Trial plan" checked={f.isTrial} onChange={(v) => ed.setForm({ ...f, isTrial: v })} />
              <PSwitch label="Public" checked={f.isPublic} onChange={(v) => ed.setForm({ ...f, isPublic: v })} />
            </View>
            <PTextInput label="Features" hint="Feature codes, comma separated." mono value={f.features.join(', ')}
              onChangeText={(v) => ed.setForm({ ...f, features: v.split(',').map((s) => s.trim()).filter(Boolean) })} />
            <GrantsEditor grants={f.grants} onChange={(g) => ed.setForm({ ...f, grants: g })} />
            <PSelect label="Status" value={f.status} onChange={(v) => ed.setForm({ ...f, status: v as CatalogStatus })} options={STATUS_OPTS} />
          </View>
        )}
      </SideDrawer>
    </Panel>
  );
};

/* ── Bundles ── */
const BundlesTab: React.FC<{ products: CatalogProduct[] }> = ({ products }) => {
  const { theme } = useTheme();
  const [rows, setRows] = useState<CatalogBundle[] | null>(null);
  const [plans, setPlans] = useState<CatalogPlan[]>([]);
  const load = useCallback(() => { catalogBundles().then(setRows, () => setRows([])); catalogPlans().then(setPlans, () => setPlans([])); }, []);
  useEffect(load, [load]);
  const ed = useEditor<CatalogBundle>(saveCatalogBundle, load);
  const f = ed.form;
  const planName = (id: string) => plans.find((p) => p.id === id)?.name ?? id;
  return (
    <Panel title="Bundles" actions={<PButton size="sm" kind="accent" icon="plus" onPress={() => ed.open({ code: '', name: '', status: 'draft', billingPriceRef: null, members: [], grants: [] }, true)}>New bundle</PButton>} bodyStyle={{ padding: 0 }}>
      {rows === null ? <ActivityIndicator /> : (
        <DataTable<CatalogBundle> rows={rows} keyExtractor={(r) => r.id ?? r.code} minWidth={700}
          columns={[
            { key: 'n', header: 'Bundle', width: 160, render: (r) => r.name },
            { key: 'm', header: 'Includes', render: (r) => (
              <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 4 }}>
                {r.members.map((m) => <Tag key={m.productCode} tone="accent">{`${m.productCode} · ${planName(m.planId)}`}</Tag>)}
              </View>
            ) },
            { key: 'price', header: 'Billing price', width: 160, render: (r) => r.billingPriceRef ?? '—' },
            { key: 's', header: 'Status', width: 100, render: (r) => <StatusBadge status={r.status} /> },
            { key: 'e', header: '', width: 80, align: 'right', render: (r) => <PButton size="sm" kind="ghost" onPress={() => ed.open(r, false)}>Edit</PButton> },
          ]} />
      )}
      <SideDrawer open={!!f} onClose={ed.close} title={ed.isNew ? 'New bundle' : `Edit ${f?.name}`} footer={<EditorFooter onClose={ed.close} onSave={ed.commit} saving={ed.saving} />}>
        {f && (
          <View style={{ gap: 14 }}>
            {ed.err && <Alert tone="danger">{ed.err}</Alert>}
            <PTextInput label="Code" mono value={f.code} editable={ed.isNew} onChangeText={(v) => ed.setForm({ ...f, code: v })} />
            <PTextInput label="Name" value={f.name} onChangeText={(v) => ed.setForm({ ...f, name: v })} />
            <PTextInput label="Billing price ref" mono value={f.billingPriceRef ?? ''} onChangeText={(v) => ed.setForm({ ...f, billingPriceRef: v || null })} />
            <Text style={{ fontSize: FS.sm, fontWeight: FW.medium, color: theme.colors.ink2 }}>Members — one plan per product</Text>
            {products.map((p) => {
              const member = f.members.find((m) => m.productCode === p.code);
              const opts = [{ value: '', label: 'Not included' }, ...plans.filter((x) => x.productCode === p.code && x.id).map((x) => ({ value: x.id!, label: x.name }))];
              return (
                <PSelect key={p.code} label={p.name} value={member?.planId ?? ''} options={opts}
                  onChange={(planId) => ed.setForm({
                    ...f,
                    members: planId
                      ? [...f.members.filter((m) => m.productCode !== p.code), { productCode: p.code, planId }]
                      : f.members.filter((m) => m.productCode !== p.code),
                  })} />
              );
            })}
            <GrantsEditor grants={f.grants} onChange={(g) => ed.setForm({ ...f, grants: g })} />
            <PSelect label="Status" value={f.status} onChange={(v) => ed.setForm({ ...f, status: v as CatalogStatus })} options={STATUS_OPTS} />
          </View>
        )}
      </SideDrawer>
    </Panel>
  );
};

/* ── Embed registrations (CC-05 §1): which origins may frame the card/drawer ── */
const EmbedsTab: React.FC<{ products: CatalogProduct[] }> = ({ products }) => {
  const [rows, setRows] = useState<EmbedRegistration[] | null>(null);
  const load = useCallback(() => { embedRegistrations().then(setRows, () => setRows([])); }, []);
  useEffect(load, [load]);
  const ed = useEditor<EmbedRegistration>(saveEmbedRegistration, load);
  const f = ed.form;
  return (
    <Panel title="Embed registrations" sub="Web origins allowed to frame the plan card and drawer, and mobile app schemes."
      actions={<PButton size="sm" kind="accent" icon="plus" onPress={() => ed.open({ productCode: products[0]?.code ?? '', surface: 'web', allowedOrigin: '', appScheme: null, status: 'active' }, true)}>New registration</PButton>}
      bodyStyle={{ padding: 0 }}>
      {rows === null ? <ActivityIndicator /> : (
        <DataTable<EmbedRegistration> rows={rows} keyExtractor={(r, i) => r.id ?? String(i)} minWidth={640}
          columns={[
            { key: 'p', header: 'Product', width: 120, render: (r) => r.productCode },
            { key: 's', header: 'Surface', width: 90, render: (r) => (r.surface === 'web' ? 'Web' : 'Mobile') },
            { key: 'o', header: 'Origin / scheme', render: (r) => r.allowedOrigin ?? (r.appScheme ? `${r.appScheme}://` : '—') },
            { key: 'st', header: 'Status', width: 100, render: (r) => <StatusBadge status={r.status} /> },
            { key: 'e', header: '', width: 80, align: 'right', render: (r) => <PButton size="sm" kind="ghost" onPress={() => ed.open(r, false)}>Edit</PButton> },
          ]} />
      )}
      <SideDrawer open={!!f} onClose={ed.close} title={ed.isNew ? 'New registration' : 'Edit registration'} footer={<EditorFooter onClose={ed.close} onSave={ed.commit} saving={ed.saving} />}>
        {f && (
          <View style={{ gap: 14 }}>
            {ed.err && <Alert tone="danger">{ed.err}</Alert>}
            <PSelect label="Product" value={f.productCode} onChange={(v) => ed.setForm({ ...f, productCode: v })} options={products.map((p) => ({ value: p.code, label: p.name }))} />
            <PSelect label="Surface" value={f.surface} onChange={(v) => ed.setForm({ ...f, surface: v as EmbedRegistration['surface'] })}
              options={[{ value: 'web', label: 'Web' }, { value: 'mobile', label: 'Mobile app' }]} />
            {f.surface === 'web' ? (
              <PTextInput label="Allowed origin" mono placeholder="https://salon.softknack.com" value={f.allowedOrigin ?? ''}
                onChangeText={(v) => ed.setForm({ ...f, allowedOrigin: v || null })} />
            ) : (
              <PTextInput label="App scheme" mono placeholder="sksalon" value={f.appScheme ?? ''} onChangeText={(v) => ed.setForm({ ...f, appScheme: v || null })} />
            )}
            <PSelect label="Status" value={f.status} onChange={(v) => ed.setForm({ ...f, status: v as EmbedRegistration['status'] })}
              options={[{ value: 'active', label: 'Active' }, { value: 'retired', label: 'Retired' }]} />
          </View>
        )}
      </SideDrawer>
    </Panel>
  );
};

const TABS = [
  { id: 'plans', label: 'Plans' },
  { id: 'bundles', label: 'Bundles' },
  { id: 'products', label: 'Products' },
  { id: 'embeds', label: 'Embed registrations' },
];

const AdminCatalog: React.FC = () => {
  const { isPlatformAdmin } = usePlatform();
  const [tab, setTab] = useState('plans');
  const [products, setProducts] = useState<CatalogProduct[]>([]);
  useEffect(() => { catalogProducts().then(setProducts, () => setProducts([])); }, [tab]);
  if (!isPlatformAdmin) return <PermissionDenied />;
  return (
    <ScrollView contentContainerStyle={{ padding: 28, maxWidth: 1200, width: '100%', alignSelf: 'center' }}>
      <SettingsHeader title="Plan catalog" sub="What every product sells. Prices live in Billing; plans point at a Billing price." />
      <Tabs tabs={TABS} value={tab} onChange={setTab} style={{ marginBottom: 16 }} />
      {tab === 'plans' && <PlansTab products={products} />}
      {tab === 'bundles' && <BundlesTab products={products} />}
      {tab === 'products' && <ProductsTab />}
      {tab === 'embeds' && <EmbedsTab products={products} />}
    </ScrollView>
  );
};

export default AdminCatalog;
